import React, {
  useCallback,
  useEffect,
  useReducer,
  useContext,
  useState
} from 'react';
import useSkipUpdate from '../../../../hooks/useSkipRender';
import fetchAPI from '../../../../utils/fetchAPI';
import { FetchCallbacksContext } from '../../../../internal-components/FetchCallbacks';
import SendWebhookView from '../view/SendWebhookView';
import useTranslations from '../../../../hooks/useTranslations';
import { AutomationsContex } from '../../../engine/FormBuilderAutomationsEngine';

const validateUrl = value => {
  return /^(?:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:[/?#]\S*)?$/i.test(
    value
  );
};

const WebhookReducer = (state, action) => {
  const { type, payload } = action;
  switch (type) {
    case 'update':
      return { ...state, ...payload };

    default:
      return state;
  }
};

const initialReducerState = {
  webhookId: ''
};

const SendWebhookEngine = ({ update = d => d, variant, ...props }) => {
  const [data, dispatch] = useReducer(WebhookReducer, initialReducerState);
  const { shouldSkipUpdate, skipUpdate } = useSkipUpdate();
  const { openSnackbar } = useContext(FetchCallbacksContext);
  const { form } = useContext(AutomationsContex);
  const t = useTranslations();
  const [webhookURL, setWebhookURL] = useState('');
  const [lastValidWebhookURL, setLastValidWebhookURL] = useState('');
  const [inputErrMsg, setInputErrMsg] = useState('');
  const [isMaskEnabled, setIsMaskEnabled] = useState(false);

  useEffect(() => {
    const updateQuery = () => {
      if (shouldSkipUpdate()) {
        return;
      }
      update({ additionalParameters: { root: data } });
    };

    updateQuery();
  }, [data]);

  useEffect(() => {
    const init = () => {
      const root =
        props.additionalParameters && props.additionalParameters.root;
      if (!root || !root.webhookId) {
        return;
      }
      const webhookId = root.webhookId;
      fetchAPI(`/scheduler/v1/webhooks/${webhookId}`).then(webhookData => {
        setWebhookURL(webhookData.url);
        setLastValidWebhookURL(webhookData.url);
      });

      skipUpdate();
      dispatch({ type: 'update', payload: { ...root } });
    };

    init();
  }, []);

  const postWebhookToDB = useCallback(() => {
    fetchAPI('/scheduler/v1/webhooks', {
      method: 'POST',
      body: {
        active: true,
        assetId: form.id,
        url: webhookURL
      },
      handleResponse: r => {
        const newWebhookId = r.headers.map.location;
        setLastValidWebhookURL(webhookURL);
        dispatch({ type: 'update', payload: { webhookId: newWebhookId } });
        openSnackbar('success', t('automation.snackbar.success.general'));
      }
    })
      .catch(e => openSnackbar('error', t('snackbars.statuses.save.err')))
      .finally(() => setIsMaskEnabled(false));
  }, [form, webhookURL]);

  const updateWebhookOnDB = useCallback(() => {
    fetchAPI(`/scheduler/v1/webhooks/${data.webhookId}`, {
      method: 'PUT',
      body: {
        active: true,
        assetId: form.id,
        url: webhookURL
      }
    })
      .then(() => {
        setLastValidWebhookURL(webhookURL);
        openSnackbar('success', t('automation.snackbar.success.general'));
      })
      .catch(e => openSnackbar('error', t('snackbars.statuses.save.err')))
      .finally(() => setIsMaskEnabled(false));
  }, [form, webhookURL, data]);

  const onWebhookInputBlur = useCallback(
    e => {
      const value = e.target.value;

      if (value === lastValidWebhookURL) {
        setIsMaskEnabled(false);
        return;
      }
      setTimeout(() => {
        if (!value || !validateUrl(value)) {
          setInputErrMsg(t('automation.webhook.snackbar.invalid'));
          openSnackbar('error', t('automation.webhook.snackbar.invalid'));
          setIsMaskEnabled(false);
          return;
        }

        if (data.webhookId) {
          updateWebhookOnDB();
        } else {
          postWebhookToDB();
        }
      }, 500);
    },
    [updateWebhookOnDB, postWebhookToDB, data, lastValidWebhookURL]
  );

  const onWebhookInputFocus = useCallback(() => {
    setInputErrMsg('');
    setIsMaskEnabled(true);
  }, []);

  const viewProps = {
    onWebhookInputBlur,
    onWebhookInputFocus,
    inputErrMsg,
    setWebhookURL,
    webhookURL,
    isMaskEnabled,
    ...props
  };

  return <SendWebhookView {...viewProps} />;
};

export default SendWebhookEngine;
